import React from 'react';
import {
  faBolt, faLayerGroup, faGaugeHigh, faBorderAll, faShieldHalved, faChartLine,
  faRuler, faWeightHanging, faPlug, faSitemap, faBatteryFull,
} from '@fortawesome/free-solid-svg-icons';
import TypeChips, { moduleTypes } from './TypeChips';
import { wierszeMocy } from '../utils/etykietaMocy';
import i18n from '../i18n';

/**
 * Wiersze sekcji „Dane produktu" — scalone dane modelu, wspólne dla strony modelu
 * i strony pojedynczej oferty.
 *
 * DLACZEGO WSPÓLNE, a nie po kopii na stronę: to ta sama tabela o tym samym
 * produkcie. Dwie kopie rozjechałyby się przy pierwszym dołożeniu parametru —
 * strona modelu dostałaby nowy wiersz, a strona oferty nie, i ten sam produkt
 * miałby dwa różne zestawy danych pod dwoma adresami.
 *
 * ZASADA: brak wartości = brak wiersza (SpecGrid odsiewa puste). Nigdy nie
 * pokazujemy „Sprawność: —" tam, gdzie po prostu nie znamy sprawności.
 */

// Etykiety wartości technicznych — surowe wartości z bazy są nieczytelne dla użytkownika.
// Teksty są w słowniku `model` (spec.zlacze.*, spec.ladowanie_pv.*, spec.rodzaj_ladowarki.*);
// tu tylko lista znanych wartości. Nieznana wartość jest pokazywana bez zmian.
const ZLACZA = ['gniazdo_Type2', 'kabel_Type2', 'kabel_Type1'];
const TRYBY_PV = ['wlasny_licznik', 'tylko_falownik_tej_marki', 'brak_funkcji', 'brak_danych'];
const RODZAJE_LADOWAREK = ['ac_wallbox', 'ac_stacja', 'dc_stacja', 'mobilna_evse'];

// wierszeMocy (utils/etykietaMocy.js) oddaje polskie etykiety i polski brak wartości,
// bo z tej samej funkcji korzysta listing. Tłumaczymy je dopiero tutaj.
const ETYKIETY_MOCY = { 'Moc DC': 'pole.moc_dc', 'Moc AC': 'pole.moc_ac', Moc: 'pole.moc' };
const BRAKI_MOCY = { 'brak danych': 'spec.brak_danych', 'brak potwierdzonych danych': 'spec.brak_potwierdzonych_danych' };

// To nie komponent, więc i18n.t zamiast haka. Wołane przy budowaniu wierszy,
// nie przy imporcie modułu.
const t = (klucz, opcje) => i18n.t(`model:${klucz}`, opcje);
const zListy = (lista, grupa, v) => (lista.includes(v) ? t(`${grupa}.${v}`) : v);
// Faza, napięcie, rodzaj i kolor ramy przychodzą z API jako wartości katalogu
// („1 Fazowy”, „Hybrydowy”). Wartość się nie zmienia, tłumaczymy tylko napis.
const wartosc = (grupa, v) => (v ? t(`wartosc.${grupa}.${v}`, { defaultValue: v }) : v);
const lata = (v) => t('spec.lata', { count: Number(v), lata: v });

const dash = (v) => (v === null || v === undefined || String(v).trim() === '' ? '—' : v);

export const buildSpecs = (m, category) => {
  if (category === 'panel') {
    const dims = [m.module_length, m.module_width, m.module_thickness].every((x) => x == null)
      ? null : `${m.module_length ?? '—'} × ${m.module_width ?? '—'} × ${m.module_thickness ?? '—'} mm`;
    return [
      { icon: faBolt, label: t('pole.moc'), value: m.power != null ? `${m.power} Wp` : null },
      // Moduł PV może mieć dwa typy (np. Bifacial + Full Black) — chipy z pola `types`.
      { icon: faLayerGroup, label: t('pole.typ'), value: moduleTypes(m).length ? <TypeChips types={m.types} type={m.type} /> : null },
      { icon: faGaugeHigh, label: t('pole.sprawnosc'), value: m.efficiency != null ? `${m.efficiency}%` : null },
      { icon: faBorderAll, label: t('pole.kolor_ramy'), value: dash(wartosc('kolor_ramy', m.frame_colour)) },
      { icon: faShieldHalved, label: t('pole.gwarancja_prod'), value: m.product_warranty != null ? lata(m.product_warranty) : null },
      { icon: faChartLine, label: t('pole.gwarancja_lin'), value: m.eff_warranty != null ? lata(m.eff_warranty) : null },
      { icon: faRuler, label: t('pole.wymiary'), value: dims },
      { icon: faWeightHanging, label: t('pole.waga'), value: m.weight != null ? `${m.weight} kg` : null },
    ];
  }
  if (category === 'inwerter') {
    return [
      { icon: faBolt, label: t('pole.moc'), value: m.power != null ? `${m.power} kW` : null },
      { icon: faLayerGroup, label: t('pole.typ'), value: dash(wartosc('typ', m.type)) },
      { icon: faPlug, label: t('pole.fazy'), value: dash(wartosc('faza', m.phase)) },
      { icon: faSitemap, label: t('pole.liczba_mppt'), value: m.mppt != null ? String(m.mppt) : null },
      { icon: faShieldHalved, label: t('pole.gwarancja'), value: dash(m.warranty) },
    ];
  }
  if (category === 'wallbox') {
    const rodzaj = RODZAJE_LADOWAREK.includes(m.product_class)
      ? t(`spec.rodzaj_ladowarki.${m.product_class}`) : undefined;
    return [
      { icon: faLayerGroup, label: t('pole.rodzaj'), value: dash(rodzaj) },
      // Przy stacji hybrydowej dwa osobne wiersze: Moc DC i Moc AC.
      ...wierszeMocy(m).map((w) => ({
        icon: faBolt,
        label: ETYKIETY_MOCY[w.label] ? t(ETYKIETY_MOCY[w.label]) : w.label,
        value: BRAKI_MOCY[w.value] ? t(BRAKI_MOCY[w.value]) : w.value,
      })),
      { icon: faPlug, label: t('pole.fazy'), value: m.phases != null ? t('spec.fazowy', { count: Number(m.phases), faz: m.phases }) : null },
      { icon: faLayerGroup, label: t('pole.zlacze'), value: dash(zListy(ZLACZA, 'spec.zlacze', m.connector_type)) },
      { icon: faRuler, label: t('pole.dlugosc_kabla'), value: m.cable_length_m != null ? `${m.cable_length_m} m` : null },
      { icon: faShieldHalved, label: t('pole.klasa_szczelnosci'), value: dash(m.ip_rating) },
      { icon: faSitemap, label: t('pole.rfid'), value: m.rfid == null ? null : (m.rfid ? t('spec.tak') : t('spec.nie')) },
      { icon: faGaugeHigh, label: t('pole.licznik_mid'), value: m.mid_meter == null ? null : (m.mid_meter ? t('spec.tak') : t('spec.nie')) },
      // „Brak potwierdzonych danych" jest pełnoprawną odpowiedzią i nie może
      // wyglądać jak „nie obsługuje" — zasada z modułu magazynów.
      { icon: faChartLine, label: t('pole.ladowanie_pv'), value: dash(zListy(TRYBY_PV, 'spec.ladowanie_pv', m.pv_mode)) },
    ];
  }
  return [
    { icon: faBatteryFull, label: t('pole.pojemnosc'), value: m.capacity != null ? `${m.capacity} kWh` : null },
    { icon: faLayerGroup, label: t('pole.typ'), value: dash(wartosc('typ', m.type)) },
    { icon: faBolt, label: t('pole.napiecie'), value: dash(wartosc('napiecie', m.voltage)) },
    { icon: faShieldHalved, label: t('pole.gwarancja'), value: dash(m.warranty) },
  ];
};

export default buildSpecs;
